import type { QueryStatus } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { queryFn } from './queryFn';
import { selector } from './selector';
import type { Entity } from './types';
import { persistedQueryConfig } from '@/utilities/persistQueryClient/utilities';
import { useCallback, useMemo } from 'react';
import { useNarrativesForCompanies } from '../useNarrativesForCompanies';
import useUser from '@/hooks/useUser';

export const QUERY_KEY = ['DASHBOARD_FEED'] as const;

interface Response {
  status: QueryStatus;
  isLoading: boolean;
  keys: string[];
  entities: Record<string, Entity>;
  isFetched: boolean;
}

export default function useFeed(): Response {
  const {
    data = { keys: [], entities: {} },
    status,
    isFetched,
    isLoading,
  } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => queryFn(),
    retry: 1,
    notifyOnChangeProps: ['data', 'error'],
    select: useCallback(selector, []),
    ...persistedQueryConfig,
  });
  const { keys, entities } = data;
  const { data: userData } = useUser();
  const { data: narratives, isLoading: isLoadingNarrativesForCompanies } =
    useNarrativesForCompanies(
      {
        companyIds: data.keys,
      },
      {
        enabled: userData.loggedIn && data.keys.length > 0,
      }
    );
  const entitiesWithNarratives = useMemo(
    () =>
      narratives?.Companies.reduce(
        (result, company) => {
          const entity = entities[company.id];
          return {
            ...result,
            [company.id]: {
              ...entity,
              companyExtra: company,
            },
          };
        },
        {} as Record<string, Entity>
      ) ?? entities,
    [entities, narratives]
  );

  return {
    keys,
    entities: entitiesWithNarratives,
    isLoading: isLoading || isLoadingNarrativesForCompanies,
    status,
    isFetched,
  };
}
